// external
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import React, { FC, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';

// internal
import useService from '../../../../../hooks/service.hook';
import TeamsService from '../../../../../services/teams.service';
import useSnackbarHelper from '../../../../../hooks/snackbar.hook';
import COESkeletonComponent from '../../../../../components/general/coe-skeleton/coe-skeleton.component';
import EditAthleteDialog from '../../../../../components/dialogs/edit-athlete.dialog';
import InviteAthletesDialog from '../../../../../components/dialogs/invite-athletes.dialog';
import WelcomeTeamInviteDialog from '../../../../../components/dialogs/welcome-team-invite.dialog';
import { sortByLastName } from '../../../../../helpers/general.helpers';
import { UserAvatar } from '../../../../../components/user-avatar/user-avatar.component';
import { palette } from '../../../../../theme';
import { Team } from '../../../../../models/training.models';
import { RosterMember, TeamJoinRequest } from '../../../../../models/team.models';

import './team-roster.tab.css';

const emptyAthlete = {
  id: '',
  email: '',
  name: '',
  role: '',
};

const getTimeAgo = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

const formatJoined = (member: RosterMember, retired = false): string | null => {
  const raw = retired ? member.retiredAt || member.joinedAt || member.createdAt : member.joinedAt || member.createdAt;
  if (!raw) return null;
  const formatted = dayjs(raw).format('MMM YYYY');
  return retired ? `Retired ${formatted}` : `Joined ${formatted}`;
};

type ConfirmKind = 'retire' | 'reactivate' | 'remove' | 'leave' | null;

interface TeamRosterTabProps {
  team: Team;
  refreshTeam: () => void;
}

const TeamRosterTab: FC<TeamRosterTabProps> = (props) => {
  const teamsService = useService(TeamsService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();
  const location = useLocation();

  const [membersLoading, setMembersLoading] = useState(true);
  const [members, setMembers] = useState<RosterMember[]>([]);
  const [retiredAthletes, setRetiredAthletes] = useState<RosterMember[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [joinRequests, setJoinRequests] = useState<TeamJoinRequest[]>([]);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(props.team.role?.toLowerCase() === 'coach');

  const [editAthleteDialogOpen, setEditAthleteDialogOpen] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState<any>(emptyAthlete);
  const [inviteAthletesDialogOpen, setInviteAthletesDialogOpen] = useState(false);
  const [deactivateInviteDialogOpen, setDeactivateInviteDialogOpen] = useState(false);

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeCode, setWelcomeCode] = useState<string | null>(props.team.inviteCode || null);
  const [generatingWelcomeCode, setGeneratingWelcomeCode] = useState(false);
  const [welcomeInviting, setWelcomeInviting] = useState(false);

  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [confirmStep, setConfirmStep] = useState<1 | 2>(1);
  const [confirmMember, setConfirmMember] = useState<RosterMember | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isCoach = props.team.role?.toLowerCase() === 'coach';
  const teamActive = props.team.status === 'active' && !props.team.billingRequired;

  const currentUserEmail = useMemo(() => {
    try {
      const raw = window.localStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return typeof u?.email === 'string' ? u.email.toLowerCase() : null;
    } catch {
      return null;
    }
  }, []);

  const isCreator =
    isCoach &&
    !!currentUserEmail &&
    !!props.team.creatorEmail &&
    props.team.creatorEmail.toLowerCase() === currentUserEmail;

  useEffect(() => {
    getMembers();
    if (isCoach) {
      getInvitations();
      getJoinRequests();
    }
  }, [props.team.id, props.team.role]);

  useEffect(() => {
    if (props.team.inviteCode) setWelcomeCode(props.team.inviteCode);
  }, [props.team.inviteCode]);

  useEffect(() => {
    const state = location.state as { welcomeInvite?: boolean } | null;
    if (!state?.welcomeInvite || !isCoach) return;
    setWelcomeOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, isCoach]);

  const getMembers = () => {
    setMembersLoading(true);
    const teamId = props.team.id as string;
    const membersRequest = teamsService.getAllTeamMembers(teamId);
    const retiredRequest = isCoach
      ? teamsService.getRetiredAthletes(teamId)
      : Promise.resolve({ data: { data: [] as RosterMember[] } });

    Promise.all([membersRequest, retiredRequest])
      .then(([membersResponse, retiredResponse]) => {
        setMembers(Array.isArray(membersResponse.data?.data) ? membersResponse.data.data : []);
        setRetiredAthletes(Array.isArray(retiredResponse.data?.data) ? retiredResponse.data.data : []);
        setMembersLoading(false);
      })
      .catch(() => {
        setMembersLoading(false);
        setMembers([]);
        setRetiredAthletes([]);
        showSnackbar('There was an error while trying to get athletes for this team.', 'error');
      });
  };

  const getInvitations = () => {
    setInvitationsLoading(true);
    teamsService
      .getTeamInvitations(props.team.id as string)
      .then((response) => {
        setInvitations(Array.isArray(response.data.data) ? response.data.data : []);
        setInvitationsLoading(false);
      })
      .catch(() => {
        setInvitationsLoading(false);
        showSnackbar('There was an error loading invitations.', 'error');
      });
  };

  const getJoinRequests = () => {
    if (!isCoach) return;
    setJoinRequestsLoading(true);
    teamsService
      .getTeamJoinRequests(props.team.id as string)
      .then((response) => {
        setJoinRequests(response.data.data ?? []);
        setJoinRequestsLoading(false);
      })
      .catch(() => {
        setJoinRequestsLoading(false);
        showSnackbar('Could not load join requests.', 'error');
      });
  };

  const generateWelcomeCode = () => {
    if (generatingWelcomeCode || welcomeCode) return;
    setGeneratingWelcomeCode(true);
    teamsService
      .postTeamInviteCode(props.team.id as string)
      .then((response) => {
        const code = response.data?.data?.inviteCode as string | undefined;
        if (code) setWelcomeCode(code);
        props.refreshTeam();
      })
      .catch(() => showSnackbar('Could not generate invite code.', 'error'))
      .finally(() => setGeneratingWelcomeCode(false));
  };

  const copyWelcomeCode = () => {
    const code = welcomeCode || props.team.inviteCode;
    if (!code) return;
    void navigator.clipboard.writeText(code).then(
      () => showSnackbar('Copied to clipboard.', 'success'),
      () => showSnackbar('Could not copy.', 'error')
    );
  };

  const handleWelcomeInvite = (athleteEmails: string[]) => {
    setWelcomeInviting(true);
    teamsService
      .inviteAthletesToTeam(props.team.id as string, athleteEmails)
      .then(() => {
        getInvitations();
        showSnackbar('Successfully invited athletes.', 'success');
      })
      .catch((err: { response?: { data?: { message?: string } } }) =>
        showSnackbar(err?.response?.data?.message || 'There was an error while trying to invite athletes.', 'error')
      )
      .finally(() => setWelcomeInviting(false));
  };

  const generateInviteCode = () => {
    teamsService
      .postTeamInviteCode(props.team.id as string)
      .then(() => {
        showSnackbar('Invite code generated.', 'success');
        props.refreshTeam();
      })
      .catch(() => showSnackbar('Could not generate invite code.', 'error'));
  };

  const handleDeactivateInviteConfirm = () => {
    teamsService
      .deleteTeamInviteCode(props.team.id as string)
      .then(() => {
        showSnackbar('Invite code deactivated.', 'success');
        setDeactivateInviteDialogOpen(false);
        props.refreshTeam();
      })
      .catch(() => {
        showSnackbar('Could not deactivate invite code.', 'error');
        setDeactivateInviteDialogOpen(false);
      });
  };

  const copyInviteCode = () => {
    const code = props.team.inviteCode;
    if (!code) return;
    void navigator.clipboard.writeText(code).then(
      () => showSnackbar('Copied to clipboard.', 'success'),
      () => showSnackbar('Could not copy.', 'error')
    );
  };

  const decideJoinRequest = (requestId: number, decision: 'approve' | 'deny') => {
    teamsService
      .putTeamJoinRequestDecision(props.team.id as string, requestId, decision)
      .then((response) => {
        showSnackbar(response.data?.message || (decision === 'approve' ? 'Approved.' : 'Denied.'), 'success');
        getJoinRequests();
        props.refreshTeam();
        getMembers();
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Could not update the request.';
        showSnackbar(typeof msg === 'string' ? msg : 'Could not update the request.', 'error');
      });
  };

  const handleInviteAthletes = (athleteEmails: string[]) => {
    setInviteAthletesDialogOpen(false);
    setInvitationsLoading(true);
    teamsService
      .inviteAthletesToTeam(props.team.id as string, athleteEmails)
      .then(() => {
        getInvitations();
        showSnackbar('Successfully invited athletes.', 'success');
      })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        setInvitationsLoading(false);
        showSnackbar(err?.response?.data?.message || 'There was an error while trying to invite athletes.', 'error');
      });
  };

  const handleResendInvitation = (email: string) => {
    teamsService
      .inviteAthletesToTeam(props.team.id as string, [email])
      .then(() => {
        showSnackbar('Invitation resent.', 'success');
        getInvitations();
      })
      .catch(() => showSnackbar('Error resending invitation.', 'error'));
  };

  const deletePlayerInvitation = (invitationId: string) => {
    teamsService
      .deactivateAthleteInvitation(props.team.id as string, invitationId)
      .then(() => {
        showSnackbar('Successfully deactivated invitation.', 'success');
        getInvitations();
      })
      .catch(() => showSnackbar('Error while deactivating invitation', 'error'));
  };

  const handleEditAthlete = (athlete: any) => {
    setEditAthleteDialogOpen(false);
    setMembersLoading(true);
    teamsService
      .updateAthlete(props.team.id as string, athlete.id, { roleId: athlete.roleId })
      .then(() => {
        getMembers();
        showSnackbar('Successfully updated athlete', 'success');
      })
      .catch(() => {
        setMembersLoading(false);
        showSnackbar('Error while updating athlete please try again', 'error');
      });
  };

  const openConfirm = (kind: ConfirmKind, member: RosterMember | null = null) => {
    setConfirmKind(kind);
    setConfirmMember(member);
    setConfirmStep(1);
  };

  const closeConfirm = () => {
    if (actionLoading) return;
    setConfirmKind(null);
    setConfirmMember(null);
    setConfirmStep(1);
  };

  const runConfirmAction = () => {
    if (!confirmKind) return;

    if (confirmStep === 1 && (confirmKind === 'retire' || confirmKind === 'remove' || confirmKind === 'leave')) {
      setConfirmStep(2);
      return;
    }

    setActionLoading(true);
    const teamId = props.team.id as string;

    const finish = (okMessage: string, then?: () => void) => {
      setActionLoading(false);
      closeConfirm();
      showSnackbar(okMessage, 'success');
      then?.();
    };

    const fail = (msg: string) => {
      setActionLoading(false);
      showSnackbar(msg, 'error');
    };

    if (confirmKind === 'retire' && confirmMember) {
      teamsService
        .updateAthlete(teamId, String(confirmMember.id), { active: false })
        .then(() => {
          finish('Athlete retired.', getMembers);
        })
        .catch(() => fail('Could not retire athlete.'));
      return;
    }

    if (confirmKind === 'reactivate' && confirmMember) {
      teamsService
        .updateAthlete(teamId, String(confirmMember.id), { active: true })
        .then(() => {
          finish('Athlete reactivated.', getMembers);
        })
        .catch((err: { response?: { data?: { message?: string } } }) =>
          fail(err?.response?.data?.message || 'Could not reactivate athlete.')
        );
      return;
    }

    if (confirmKind === 'remove' && confirmMember) {
      teamsService
        .removeTeamMember(teamId, String(confirmMember.id))
        .then(() => {
          finish('Member removed from team', getMembers);
        })
        .catch(() => fail('Error removing member, please try again'));
      return;
    }

    if (confirmKind === 'leave') {
      teamsService
        .leaveTeam(teamId)
        .then(() => {
          finish('You have left the team', () => navigate('/teams'));
        })
        .catch(() => fail('There was an error leaving the team, please try again'));
    }
  };

  const isSelf = (member: RosterMember) =>
    currentUserEmail != null && typeof member.email === 'string' && member.email.toLowerCase() === currentUserEmail;

  const coaches = useMemo(() => members.filter((m) => m.role?.toLowerCase() === 'coach'), [members]);

  const athletesList = useMemo(
    () =>
      sortByLastName(
        members.filter((m) => m.role?.toLowerCase() !== 'coach').map((m) => ({ ...m, name: m?.name ?? '' }))
      ) as RosterMember[],
    [members]
  );

  const confirmCopy = useMemo(() => {
    const name = confirmMember?.name || 'this member';
    switch (confirmKind) {
      case 'retire':
        return confirmStep === 1
          ? {
              title: `Retire ${name}?`,
              body: 'This can be undone later. They will stop receiving plans, schedules, and team messages.',
              confirm: 'Continue',
            }
          : {
              title: 'Confirm retire',
              body: `${name} will move to Retired and stop receiving team activity immediately.`,
              confirm: 'Retire',
            };
      case 'reactivate':
        return {
          title: `Reactivate ${name}?`,
          body: 'They will return to the active roster and start receiving team activity again.',
          confirm: 'Reactivate',
        };
      case 'remove':
        return confirmStep === 1
          ? {
              title: `Remove ${name}?`,
              body: 'This is permanent. They will need to be re-onboarded to rejoin the team.',
              confirm: 'Continue',
            }
          : {
              title: 'Confirm remove',
              body: `${name} will be permanently removed from the team and lose access to all team content.`,
              confirm: 'Remove',
            };
      case 'leave':
        return confirmStep === 1
          ? {
              title: 'Leave team?',
              body: 'You will lose access to this team. This cannot be undone without a new invite.',
              confirm: 'Continue',
            }
          : {
              title: 'Confirm leave',
              body: 'You will permanently leave this team and lose access to all team content.',
              confirm: 'Leave Team',
            };
      default:
        return { title: '', body: '', confirm: 'Confirm' };
    }
  }, [confirmKind, confirmStep, confirmMember]);

  const skeletonCards = (
    <>
      <COESkeletonComponent />
      <COESkeletonComponent />
    </>
  );

  const renderMemberCard = (member: RosterMember, variant: 'active' | 'retired') => {
    const self = isSelf(member);
    const isAthlete = member.role?.toLowerCase() !== 'coach';
    /** Athletes: any coach. Other coaches: team creator only. */
    const canRetireOrRemove = isAthlete || isCreator;
    const joinedLabel = formatJoined(member, variant === 'retired');
    const guardians = Array.isArray(member.guardians) ? member.guardians : [];

    return (
      <div key={String(member.id)} className={`rm-card${variant === 'retired' ? ' rm-card--retired' : ''}`}>
        <div className="rm-header">
          <UserAvatar src={member.profileImage} name={member.name || ''} size={34} fontSize="0.75rem" />
          <div style={{ minWidth: 0 }}>
            <div className="rm-name-row">
              <span className="rm-name">{member.name}</span>
              {self ? <span className="rm-me-badge">Me</span> : null}
            </div>
            {joinedLabel ? <div className="rm-joined">{joinedLabel}</div> : null}
          </div>
        </div>

        {member.email ? (
          <div className="rm-details">
            <div className="rm-detail">
              <EmailOutlinedIcon sx={{ fontSize: 14, color: palette.grey[400] }} />
              <span>{member.email}</span>
            </div>
          </div>
        ) : null}

        {isAthlete && guardians.length > 0 ? (
          <div className="rm-guardians">
            <div className="rm-guard-label">Guardians</div>
            {guardians.map((g) => (
              <div key={String(g.id)} className="rm-guard">
                <div className="rm-guard-name">{g.name}</div>
                {g.email ? <div className="rm-guard-contact">{g.email}</div> : null}
              </div>
            ))}
          </div>
        ) : null}

        {isCoach || self ? (
          <div className="rm-actions">
            {variant === 'retired' ? (
              isCoach ? (
                <Button
                  className="rm-btn-success"
                  disabled={!teamActive}
                  onClick={() => openConfirm('reactivate', member)}
                >
                  Reactivate
                </Button>
              ) : null
            ) : (
              <>
                {isCoach ? (
                  <Button
                    className="rm-btn-edit"
                    disabled={!teamActive}
                    onClick={() => {
                      setSelectedAthlete(member);
                      setEditAthleteDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                ) : null}
                {self ? (
                  <Button
                    className="rm-btn-danger"
                    disabled={!teamActive}
                    startIcon={<LogoutOutlinedIcon sx={{ fontSize: 14 }} />}
                    onClick={() => openConfirm('leave', member)}
                  >
                    Leave Team
                  </Button>
                ) : isCoach && canRetireOrRemove ? (
                  <>
                    <Button
                      className="rm-btn-danger"
                      disabled={!teamActive}
                      onClick={() => openConfirm('retire', member)}
                    >
                      Retire
                    </Button>
                    <Button
                      className="rm-btn-danger"
                      disabled={!teamActive}
                      onClick={() => openConfirm('remove', member)}
                    >
                      Remove
                    </Button>
                  </>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderSection = (
    title: string,
    count: number,
    loading: boolean,
    children: React.ReactNode,
    emptyText: string
  ) => (
    <div className="team-roster-section">
      <div className="team-roster-section-header">
        <h3>
          {title}
          {!loading ? <span className="team-roster-count">{count}</span> : null}
        </h3>
      </div>
      <div className="team-roster-grid">
        {loading ? skeletonCards : count === 0 ? <div className="team-roster-empty">{emptyText}</div> : children}
      </div>
    </div>
  );

  return (
    <>
      <div className="team-roster-page">
        {isCoach ? (
          <div className="team-roster-invite-bar">
            <Button
              className="team-roster-invite-btn"
              variant="contained"
              onClick={() => setInviteAthletesDialogOpen(true)}
              disabled={!teamActive}
            >
              + Invite Member
            </Button>
            {props.team.inviteCode ? (
              <div className="team-roster-team-code">
                <span>
                  Team Code: <strong>{props.team.inviteCode}</strong>
                </span>
                <Button size="small" variant="outlined" onClick={copyInviteCode} disabled={!teamActive}>
                  Copy
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  onClick={() => setDeactivateInviteDialogOpen(true)}
                  disabled={!teamActive}
                >
                  Deactivate
                </Button>
              </div>
            ) : (
              <Button variant="outlined" onClick={generateInviteCode} disabled={!teamActive}>
                Generate invite code
              </Button>
            )}
          </div>
        ) : props.team.inviteCode ? (
          <div className="team-roster-invite-bar">
            <div className="team-roster-team-code">
              <span>
                Team Code: <strong>{props.team.inviteCode}</strong>
              </span>
              <Button size="small" variant="outlined" onClick={copyInviteCode}>
                Copy
              </Button>
            </div>
          </div>
        ) : null}

        {renderSection(
          'Active Coaches',
          coaches.length,
          membersLoading,
          coaches.map((m) => renderMemberCard(m, 'active')),
          'No coaches on this team.'
        )}

        {renderSection(
          'Active Athletes',
          athletesList.length,
          membersLoading,
          athletesList.map((m) => renderMemberCard(m, 'active')),
          'No athletes on this team.'
        )}

        {isCoach
          ? renderSection(
              'Retired',
              retiredAthletes.length,
              membersLoading,
              retiredAthletes.map((m) => renderMemberCard(m, 'retired')),
              'No retired athletes.'
            )
          : null}

        {isCoach
          ? renderSection(
              'Pending Invites',
              invitations.length,
              invitationsLoading,
              invitations.map((item, index) => (
                <div key={item.id ?? index} className="rm-card rm-card--pending">
                  <div className="rm-header">
                    <UserAvatar name={item.email || ''} size={34} fontSize="0.75rem" />
                    <div style={{ minWidth: 0 }}>
                      <div className="rm-name">{item.email}</div>
                      <div className="rm-joined">
                        {[
                          item.role
                            ? `Invited as ${String(item.role).charAt(0).toUpperCase()}${String(item.role).slice(1)}`
                            : 'Invited',
                          item.createdAt ? getTimeAgo(item.createdAt) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </div>
                  <div className="rm-actions">
                    <Button
                      className="rm-btn-primary-outline"
                      disabled={!teamActive}
                      onClick={() => handleResendInvitation(item.email)}
                    >
                      Resend Invite
                    </Button>
                    <Button
                      className="rm-btn-danger"
                      disabled={!teamActive}
                      onClick={() => deletePlayerInvitation(item.id)}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              )),
              'No pending invites.'
            )
          : null}

        {isCoach
          ? renderSection(
              'Join Requests',
              joinRequests.length,
              joinRequestsLoading,
              joinRequests.map((req) => (
                <div key={req.id} className="rm-card">
                  <div className="rm-header">
                    <UserAvatar
                      firstname={req.requesterFirstname}
                      lastname={req.requesterLastname}
                      size={34}
                      fontSize="0.75rem"
                    />
                    <div style={{ minWidth: 0 }}>
                      <div className="rm-name">
                        {req.requesterFirstname} {req.requesterLastname}
                      </div>
                      <div className="rm-joined">
                        {req.requesterEmail}
                        {req.requestType === 'guardian' && req.athleteEmail ? ` · for ${req.athleteEmail}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="rm-actions">
                    <Button
                      className="rm-btn-success"
                      disabled={!teamActive}
                      onClick={() => decideJoinRequest(req.id, 'approve')}
                    >
                      Approve
                    </Button>
                    <Button
                      className="rm-btn-danger"
                      disabled={!teamActive}
                      onClick={() => decideJoinRequest(req.id, 'deny')}
                    >
                      Deny
                    </Button>
                  </div>
                </div>
              )),
              'No join requests.'
            )
          : null}
      </div>

      <Dialog open={Boolean(confirmKind)} onClose={closeConfirm}>
        <DialogTitle>{confirmCopy.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmCopy.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            color={confirmKind === 'reactivate' ? 'primary' : 'error'}
            variant="contained"
            disabled={actionLoading}
            onClick={runConfirmAction}
          >
            {confirmCopy.confirm}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deactivateInviteDialogOpen} onClose={() => setDeactivateInviteDialogOpen(false)}>
        <DialogTitle>Deactivate invite code?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will invalidate the current code. Anyone with the old link will no longer be able to use it to request
            joining the team.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateInviteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeactivateInviteConfirm}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {editAthleteDialogOpen && (
        <EditAthleteDialog
          isLoading={false}
          handleClose={() => setEditAthleteDialogOpen(false)}
          handleSubmit={handleEditAthlete}
          isOpen={editAthleteDialogOpen}
          athlete={selectedAthlete}
          hideMemberControls={isSelf(selectedAthlete)}
          handleRemove={
            isCoach && !isSelf(selectedAthlete) && (selectedAthlete.role?.toLowerCase() !== 'coach' || isCreator)
              ? () => {
                  setEditAthleteDialogOpen(false);
                  openConfirm('remove', selectedAthlete);
                }
              : undefined
          }
        />
      )}

      <WelcomeTeamInviteDialog
        open={welcomeOpen}
        teamName={props.team.name}
        inviteCode={welcomeCode || props.team.inviteCode || null}
        generatingCode={generatingWelcomeCode}
        inviting={welcomeInviting}
        onGenerateCode={generateWelcomeCode}
        onCopyCode={copyWelcomeCode}
        onInvite={handleWelcomeInvite}
        onClose={() => setWelcomeOpen(false)}
      />
      <InviteAthletesDialog
        isLoading={false}
        handleClose={() => setInviteAthletesDialogOpen(false)}
        handleSubmit={handleInviteAthletes}
        isOpen={inviteAthletesDialogOpen}
      />
    </>
  );
};

export default TeamRosterTab;
